import React, { useCallback, useState } from "react";
import UploadDropzone from "./UploadDropzone";
import MediaPreview from "./MediaPreview";
import { uploadFile } from "./utils";

interface MediaUploadProps {
  uploadedUrls: string[];
  onChange: (urls: string[]) => void;

  maxFiles?: number;
  maxSizeMB?: number;
  acceptedFormats?: string[];

  removable?: boolean;
  showPreview?: boolean;
}

interface LoadingStates {
  [url: string]: boolean;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  uploadedUrls,
  onChange,

  maxFiles = 10,
  maxSizeMB = 50,
  removable = true,
  showPreview = true,

  acceptedFormats = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/jpg",
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/quicktime",
  ],
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const [uploading, setUploading] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);

  const [processingMessage, setProcessingMessage] = useState("");

  const [loadingStates, setLoadingStates] = useState<LoadingStates>({});

  const handleMediaLoaded = useCallback((url: string) => {
    setLoadingStates((prev) => ({
      ...prev,
      [url]: false,
    }));
  }, []);

  const handleRemove = useCallback(
    (index: number) => {
      const url = uploadedUrls[index];

      setLoadingStates((prev) => {
        const next = { ...prev };
        delete next[url];
        return next;
      });

      onChange(uploadedUrls.filter((_, i) => i !== index));
    },
    [uploadedUrls, onChange],
  );

  const handleFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList) return;

      const files = Array.from(fileList);

      const remainingSlots = maxFiles - uploadedUrls.length;

      if (remainingSlots <= 0) {
        alert(`Maximum ${maxFiles} files allowed.`);
        return;
      }

      const validFiles = files.slice(0, remainingSlots).filter((file) => {
        if (!acceptedFormats.includes(file.type)) {
          alert(`${file.name} is not a supported format.`);
          return false;
        }

        if (file.size > maxSizeMB * 1024 * 1024) {
          alert(`${file.name} exceeds ${maxSizeMB} MB.`);
          return false;
        }

        return true;
      });

      if (!validFiles.length) return;

      setUploading(true);
      setUploadProgress(0);
      setCurrentFileIndex(0);
      setTotalFiles(validFiles.length);

      try {
        const uploaded: string[] = [];

        for (const [i, file] of validFiles.entries()) {
          setCurrentFileIndex(i);

          const result = await uploadFile({
            file,
            onProgress: setUploadProgress,
            onStatusChange: setProcessingMessage,
          });

          uploaded.push(result.url);

          setLoadingStates((prev) => ({
            ...prev,
            [result.url]: true,
          }));
        }

        onChange([...uploadedUrls, ...uploaded]);
      } catch (error) {
        console.error(error);
        alert("Failed to upload one or more files.");
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setCurrentFileIndex(0);
        setTotalFiles(0);
        setProcessingMessage("");
      }
    },
    [acceptedFormats, maxFiles, maxSizeMB, uploadedUrls, onChange],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();

      setIsDragging(false);

      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  return (
    <div className="space-y-6">
      {showPreview && (
        <MediaPreview
          urls={uploadedUrls}
          loadingStates={loadingStates}
          onLoad={handleMediaLoaded}
          onRemove={handleRemove}
          removable={removable}
        />
      )}
      <UploadDropzone
        acceptedFormats={acceptedFormats}
        maxSizeMB={maxSizeMB}
        isDragging={isDragging}
        uploading={uploading}
        uploadProgress={uploadProgress}
        currentFileIndex={currentFileIndex}
        totalFiles={totalFiles}
        processingMessage={processingMessage}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onFileChange={handleFiles}
      />
    </div>
  );
};

export default MediaUpload;
